import { useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import {
  useBalanceComprobacion,
  useEstadoResultados,
  useLibroCompras,
  useLibroVentas,
  useRetenciones,
  useRetencionesVentas,
} from '../api/queries-finanzas';
import { FinanceDemoNotice, TabBar } from '../components/FinanceBits';
import { formatDate, formatMoney, todayIso } from '../lib/format';
import { downloadCsv } from '../lib/csv';
import { descargarArchivo } from '../api/client';
import { useToast } from '../ui/ToastProvider';

type ReportTab =
  | 'ventas'
  | 'compras'
  | 'retenciones'
  | 'retencionesVentas'
  | 'balance'
  | 'resultados';

const TABS_FISCALES: ReportTab[] = ['ventas', 'compras', 'retenciones'];

interface Column {
  key: string;
  label: string;
  money?: 'USD' | 'VES';
  date?: boolean;
  align?: 'right';
}

const COLUMNS: Record<ReportTab, Column[]> = {
  ventas: [
    { key: 'fecha', label: 'Fecha', date: true },
    { key: 'documento', label: 'Doc.' },
    { key: 'numero', label: 'Factura' },
    { key: 'numero_control', label: 'Control' },
    { key: 'cliente_nombre', label: 'Cliente' },
    { key: 'cliente_rif', label: 'RIF' },
    { key: 'base_ves', label: 'Base Bs.', money: 'VES', align: 'right' },
    { key: 'iva_ves', label: 'IVA Bs.', money: 'VES', align: 'right' },
    { key: 'total_ves', label: 'Total Bs.', money: 'VES', align: 'right' },
  ],
  compras: [
    { key: 'fecha', label: 'Fecha', date: true },
    { key: 'numero_interno', label: 'Interno' },
    { key: 'numero_factura', label: 'Factura' },
    { key: 'proveedor_nombre', label: 'Proveedor' },
    { key: 'proveedor_rif', label: 'RIF' },
    { key: 'gravado_ves', label: 'Gravado Bs.', money: 'VES', align: 'right' },
    { key: 'exento_ves', label: 'Exento Bs.', money: 'VES', align: 'right' },
    { key: 'iva_ves', label: 'IVA Bs.', money: 'VES', align: 'right' },
    { key: 'total_ves', label: 'Total Bs.', money: 'VES', align: 'right' },
  ],
  retenciones: [
    { key: 'fecha', label: 'Fecha', date: true },
    { key: 'numero', label: 'Pago' },
    { key: 'proveedor_nombre', label: 'Proveedor' },
    { key: 'proveedor_rif', label: 'RIF' },
    { key: 'numero_factura', label: 'Factura' },
    { key: 'comprobante_islr', label: 'Comprobante' },
    { key: 'retencion_islr_ves', label: 'ISLR Bs.', money: 'VES', align: 'right' },
    { key: 'retencion_iva_ves', label: 'IVA ret. Bs.', money: 'VES', align: 'right' },
  ],
  retencionesVentas: [
    { key: 'fecha', label: 'Fecha', date: true },
    { key: 'numero', label: 'Recibo' },
    { key: 'cliente_nombre', label: 'Cliente' },
    { key: 'cliente_rif', label: 'RIF' },
    { key: 'numero_factura', label: 'Factura' },
    { key: 'comprobante_islr', label: 'Comprobante' },
    { key: 'retencion_islr_pct', label: '% ISLR', align: 'right' },
    { key: 'retencion_islr_ves', label: 'ISLR Bs.', money: 'VES', align: 'right' },
  ],
  balance: [
    { key: 'codigo', label: 'Código' },
    { key: 'nombre', label: 'Cuenta' },
    { key: 'tipo', label: 'Tipo' },
    { key: 'debe_ves', label: 'Debe Bs.', money: 'VES', align: 'right' },
    { key: 'haber_ves', label: 'Haber Bs.', money: 'VES', align: 'right' },
    { key: 'saldo_ves', label: 'Saldo Bs.', money: 'VES', align: 'right' },
  ],
  resultados: [
    { key: 'tipo', label: 'Tipo' },
    { key: 'codigo', label: 'Código' },
    { key: 'nombre', label: 'Cuenta' },
    { key: 'saldo_ves', label: 'Saldo Bs.', money: 'VES', align: 'right' },
    { key: 'saldo_usd', label: 'Saldo USD', money: 'USD', align: 'right' },
  ],
};

function cell(value: unknown, column: Column): string {
  if (value === null || value === undefined || value === '') return '—';
  if (column.date) return formatDate(String(value));
  if (column.money) return formatMoney(Number(value), column.money);
  return String(value);
}

export function ReportesPage() {
  const { demo } = useAuth();
  const { notify } = useToast();
  const [exporting, setExporting] = useState<'pdf' | 'xlsx' | null>(null);
  const [tab, setTab] = useState<ReportTab>('ventas');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const ventas = useLibroVentas(desde || undefined, hasta || undefined);
  const compras = useLibroCompras(desde || undefined, hasta || undefined);
  const retenciones = useRetenciones(desde || undefined, hasta || undefined);
  const retencionesVentas = useRetencionesVentas(desde || undefined, hasta || undefined);
  const balance = useBalanceComprobacion(desde || undefined, hasta || undefined);
  const resultados = useEstadoResultados(desde || undefined, hasta || undefined);
  const active =
    tab === 'ventas'
      ? ventas
      : tab === 'compras'
        ? compras
        : tab === 'retenciones'
          ? retenciones
          : tab === 'retencionesVentas'
            ? retencionesVentas
            : tab === 'balance'
              ? balance
              : resultados;
  const rows = (active.data?.data ?? []) as Array<Record<string, unknown>>;
  const totals = active.data?.totales ?? {};
  const columns = COLUMNS[tab];

  const exportar = () => {
    const hoy = todayIso();
    const nombre = `reporte-${tab}-${desde || 'inicio'}-${hasta || hoy}.csv`;
    downloadCsv(
      nombre,
      columns.map((column) => ({ key: column.key, label: column.label })),
      rows,
    );
  };

  const exportarNativo = async (formato: 'pdf' | 'xlsx') => {
    if (!TABS_FISCALES.includes(tab)) {
      exportar();
      return;
    }
    const reporte =
      tab === 'ventas' ? 'libro-ventas' : tab === 'compras' ? 'libro-compras' : 'retenciones-islr';
    const params = new URLSearchParams({ formato });
    if (desde) params.set('desde', desde);
    if (hasta) params.set('hasta', hasta);
    setExporting(formato);
    try {
      const blob = await descargarArchivo(`/api/reportes/${reporte}/export?${params.toString()}`);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reporte}-${desde || 'inicio'}-${hasta || todayIso()}.${formato}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo exportar el reporte.', 'error');
    } finally {
      setExporting(null);
    }
  };

  const totalCards = (() => {
    if (tab === 'ventas') {
      return [
        { label: 'Base Bs.', value: formatMoney(Number(totals.baseVes ?? 0), 'VES') },
        { label: 'IVA Bs.', value: formatMoney(Number(totals.ivaVes ?? 0), 'VES') },
        { label: 'Total Bs.', value: formatMoney(Number(totals.totalVes ?? 0), 'VES') },
      ];
    }
    if (tab === 'compras') {
      return [
        { label: 'Base Bs.', value: formatMoney(Number(totals.baseVes ?? 0), 'VES') },
        { label: 'IVA crédito', value: formatMoney(Number(totals.ivaVes ?? 0), 'VES') },
        { label: 'Total Bs.', value: formatMoney(Number(totals.totalVes ?? 0), 'VES') },
      ];
    }
    if (tab === 'retenciones') {
      return [
        { label: 'ISLR retenido', value: formatMoney(Number(totals.islrVes ?? 0), 'VES') },
        { label: 'IVA retenido', value: formatMoney(Number(totals.ivaVes ?? 0), 'VES') },
        {
          label: 'Total retenido',
          value: formatMoney(Number(totals.islrVes ?? 0) + Number(totals.ivaVes ?? 0), 'VES'),
        },
      ];
    }
    if (tab === 'retencionesVentas') {
      return [
        { label: 'ISLR retenido', value: formatMoney(Number(totals.islrVes ?? 0), 'VES') },
        { label: 'En USD', value: formatMoney(Number(totals.islrUsd ?? 0), 'USD') },
        { label: 'Recibos', value: String(totals.documentos ?? 0) },
      ];
    }
    if (tab === 'balance') {
      return [
        { label: 'Debe Bs.', value: formatMoney(Number(totals.debeVes ?? 0), 'VES') },
        { label: 'Haber Bs.', value: formatMoney(Number(totals.haberVes ?? 0), 'VES') },
        {
          label: 'Diferencia',
          value: formatMoney(
            Number(totals.debeVes ?? 0) - Number(totals.haberVes ?? 0),
            'VES',
          ),
        },
      ];
    }
    return [
      { label: 'Ingresos', value: formatMoney(Number(totals.ingresosVes ?? 0), 'VES') },
      { label: 'Costos + gastos', value: formatMoney(Number(totals.costosVes ?? 0) + Number(totals.gastosVes ?? 0), 'VES') },
      {
        label: 'Utilidad',
        value: formatMoney(Number(totals.utilidadVes ?? 0), 'VES'),
      },
    ];
  })();

  return (
    <div className="dashboard">
      <header className="page-head">
        <div>
          <p className="eyebrow">Contabilidad y fiscal</p>
          <h1 className="page-title">Libros, retenciones y estados</h1>
          <p className="page-sub">
            Libros fiscales, retenciones de clientes y proveedores, balance de comprobación y
            estado de resultados del período.
          </p>
        </div>
      </header>

      <FinanceDemoNotice demo={demo} />

      <div className="page-tabs-row">
        <TabBar
          label="Reporte"
          value={tab}
          onChange={setTab}
          tabs={[
            { value: 'ventas', label: 'Libro de ventas' },
            { value: 'compras', label: 'Libro de compras' },
            { value: 'retenciones', label: 'Retención proveedores' },
            { value: 'retencionesVentas', label: 'Retención clientes' },
            { value: 'balance', label: 'Balance de comprobación' },
            { value: 'resultados', label: 'Estado de resultados' },
          ]}
        />
        <div className="filter-controls">
          <input type="date" className="input input--date" value={desde} onChange={(e) => setDesde(e.target.value)} aria-label="Desde" />
          <input type="date" className="input input--date" value={hasta} onChange={(e) => setHasta(e.target.value)} aria-label="Hasta" />
        </div>
      </div>

      <div className="summary-strip">
        {totalCards.map((card) => (
          <div className="summary-tile" key={card.label}>
            <span>{card.label}</span>
            <strong>{card.value}</strong>
            <small>Período {active.data?.desde ?? '—'} → {active.data?.hasta ?? '—'}</small>
          </div>
        ))}
      </div>

      <section className="card transactions-card">
        <div className="table-scroll">
          <table className="data-table data-table--compact">
            <thead>
              <tr>{columns.map((column) => <th key={column.key} className={column.align === 'right' ? 'align-right' : undefined}>{column.label}</th>)}</tr>
            </thead>
            <tbody>
              {active.isLoading ? <tr><td colSpan={columns.length}><div className="table-empty">Cargando reporte…</div></td></tr> : null}
              {!active.isLoading && rows.length === 0 ? <tr><td colSpan={columns.length}><div className="table-empty">Sin movimientos en el período.</div></td></tr> : null}
              {rows.map((row, index) => (
                <tr key={`${tab}-${index}-${String(row.numero ?? row.id ?? index)}`}>
                  {columns.map((column) => (
                    <td key={column.key} className={column.align === 'right' ? 'align-right' : undefined}>
                      {cell(row[column.key], column)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <footer className="table-footer">
          <span>
            {rows.length}{' '}
            {tab === 'balance' || tab === 'resultados' ? 'cuentas' : 'documentos'} ·{' '}
            {tab === 'retenciones'
              ? 'Comprobantes de retención de proveedores'
              : tab === 'retencionesVentas'
                ? 'ISLR retenido por clientes'
                : tab === 'balance'
                  ? 'Movimientos del período por cuenta'
                  : tab === 'resultados'
                    ? 'Ingresos, costos y gastos del período'
                    : 'Documentos no anulados'}
          </span>
          <div className="row-actions">
            {TABS_FISCALES.includes(tab) ? (
              <>
                <button type="button" className="btn btn--outline btn--sm" onClick={() => void exportarNativo('pdf')} disabled={rows.length === 0 || exporting !== null}>
                  {exporting === 'pdf' ? 'Generando…' : 'PDF'}
                </button>
                <button type="button" className="btn btn--outline btn--sm" onClick={() => void exportarNativo('xlsx')} disabled={rows.length === 0 || exporting !== null}>
                  {exporting === 'xlsx' ? 'Generando…' : 'XLSX'}
                </button>
              </>
            ) : null}
            <button type="button" className="btn btn--outline btn--sm" onClick={exportar} disabled={rows.length === 0}>
              CSV
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
}
